import { Link } from "react-router-dom";
import { buttonClasses, useBrand } from "@fprint/ui";

import { SignOutButton, useAuth } from "../lib/auth";

/** PR #42 iPhone rule: stack full-width below `sm`, row from `sm` up. */
export const CUSTOMER_CTA_ROW_CLASS =
  "flex w-full max-w-sm flex-col gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:items-center";

export const CUSTOMER_CTA_CLASS = "w-full sm:w-auto";

export const CUSTOMER_HEADER_CLASS =
  "flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between";

/** Quote page: centered brand, CTAs in the right column (PR #42 3-col). */
export const CUSTOMER_BRAND_HEADER_CLASS =
  "flex flex-col items-center gap-3 sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-start sm:gap-3";

function CustomerCtaRow({
  isSignedIn,
  testId,
  className,
}: {
  isSignedIn: boolean;
  testId: string;
  className?: string;
}) {
  return (
    <div
      className={className ? `${CUSTOMER_CTA_ROW_CLASS} ${className}` : CUSTOMER_CTA_ROW_CLASS}
      data-testid={testId}
    >
      {isSignedIn ? (
        <Link
          to="/orders"
          className={buttonClasses({
            variant: "secondary",
            className: CUSTOMER_CTA_CLASS,
          })}
        >
          My orders
        </Link>
      ) : null}
      <Link
        to="/quote"
        className={buttonClasses({
          variant: "secondary",
          className: CUSTOMER_CTA_CLASS,
        })}
      >
        New quote
      </Link>
      <SignOutButton className={CUSTOMER_CTA_CLASS} />
    </div>
  );
}

/**
 * Shared customer chrome for quote / order detail pages.
 *
 * Always exposes **My orders** (when signed in) and **New quote**, plus an
 * in-flow Sign out. Never `absolute` — that overlap is UI-11 / PR #42.
 *
 * - `page` (default): page title + optional brand home link; stack/row like Orders.
 * - `brand`: centered wordmark (Quote 3-col grid) with CTAs on the right.
 */
export function CustomerHeader({
  title,
  subtitle,
  variant = "page",
  titleTestId,
  headerTestId = "customer-header",
  ctaTestId = "customer-header-cta-row",
}: {
  title?: string;
  subtitle?: string;
  variant?: "page" | "brand";
  titleTestId?: string;
  headerTestId?: string;
  ctaTestId?: string;
}) {
  const brand = useBrand();
  const { isSignedIn } = useAuth();
  const heading = title ?? brand.name;
  const headingIsBrand = title == null;

  if (variant === "brand") {
    return (
      <header className={CUSTOMER_BRAND_HEADER_CLASS} data-testid={headerTestId}>
        <div className="hidden sm:col-start-1 sm:block" aria-hidden="true" />
        <div className="flex flex-col gap-2 text-center sm:col-start-2">
          <Link
            to="/"
            className="text-3xl font-bold text-fg"
            data-testid={titleTestId ?? "brand-name"}
          >
            {brand.name}
          </Link>
          {subtitle ? <p className="text-muted-fg">{subtitle}</p> : null}
        </div>
        <CustomerCtaRow
          isSignedIn={isSignedIn}
          testId={ctaTestId}
          className="sm:col-start-3 sm:justify-self-end"
        />
      </header>
    );
  }

  return (
    <header className={CUSTOMER_HEADER_CLASS} data-testid={headerTestId}>
      <div className="flex flex-col gap-2">
        {!headingIsBrand && (
          <Link
            to="/"
            className="text-sm font-medium text-muted-fg hover:underline"
            data-testid="customer-home-link"
          >
            {brand.name}
          </Link>
        )}
        {headingIsBrand ? (
          <Link
            to="/"
            className="text-3xl font-bold text-fg"
            data-testid={titleTestId ?? "brand-name"}
          >
            {heading}
          </Link>
        ) : (
          <h1 className="text-3xl font-bold text-fg" data-testid={titleTestId}>
            {heading}
          </h1>
        )}
        {subtitle ? <p className="text-muted-fg">{subtitle}</p> : null}
      </div>
      <CustomerCtaRow isSignedIn={isSignedIn} testId={ctaTestId} />
    </header>
  );
}
